import React, {useContext, useMemo, useState} from "react";
import IdentityImage from "../../components/IdentityImage.jsx";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCrown} from "@fortawesome/free-solid-svg-icons";
import Button from "../../components/Button.jsx";
import {useTranslation} from "react-i18next";
import YesNo from "../../components/YesNo.jsx";
import useDownloadState from "../../api/utilities/useDownloadState.js";
import {ProjectApi, ProjectPermission} from "../../api/authority/generated/index";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import toast from "react-hot-toast";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import Badge from "../../components/Badge.jsx";
import {useIsAdministrator} from "../../components/auth/rolesAndPermissions.js";
import ActionButton from "../../components/ActionButton.jsx";
import {formatDateTimeLabel} from "../admin/ReportingLab.jsx";
import ProjectRoleSelect from "./ProjectRoleSelect.jsx";
import {buildProjectRoleValues, OWNER_ROLE_VALUE} from "./projectRoleOptions.js";

export {buildProjectRoleValues, OWNER_ROLE_VALUE} from "./projectRoleOptions.js";

export function resolveProjectRolesResponse(response) {
    if (!Array.isArray(response)) {
        return {
            roles: [],
            error: new TypeError('Project roles response must be an array.'),
            canRenderUsers: false,
        };
    }

    return {
        roles: response,
        error: null,
        canRenderUsers: true,
    };
}

export default function ProjectUser({project, projectRoles, projectUser}) {

    let {t} = useTranslation();
    let {et} = useErrorTranslation();

    const [isOwner, setIsOwner] = useState(projectUser.isOwner === true);

    let {context} = useContext(GlobalContext);

    let [deleteQuestionVisible, setDeleteQuestionVisible] = useState(false);

    let [deleteState, setDeleteDownloading, setDeleteResult, setDeleteError] = useDownloadState();
    let [deleted, setDeleted] = useState(false);

    let {getAuthorityConfiguration} = useContext(GlobalContext)

    let removeUser = () => {
        setDeleteDownloading(true);

        new ProjectApi(getAuthorityConfiguration())
            .removeProjectUser({
                projectId: project.id,
                removeUserInput: {
                    userId: projectUser.user.id,
                }
            })
            .then(() => {
                setDeleted(true);
                toast.success(t('project.user-successfully-removed'))
            })
            .catch((e) => et(e).then((message) => { if (message) toast.error(message); }))
            .finally(() => setDeleteDownloading(false))
        ;
    };

    let isAdministrator = useIsAdministrator();

    let canDelete = isAdministrator || (project.isOwner && !projectUser.isOwner);

    let canToggleOwner = isAdministrator || (
        project.isOwner && context.currentIdentity?.user.id !== projectUser.user.id
    );

    let canChangeRole = isAdministrator || project.isOwner;

    let projectRoleValues = useMemo(
        () => buildProjectRoleValues(projectRoles, t),
        [project.id, projectRoles, t]
    );

    const [roleValue, setRoleValue] = useState(null);

    let defaultRoleValue = useMemo(
        () => {
            for(let possibleDefaultValue of projectRoleValues) {
                if(possibleDefaultValue.value === (projectUser.role?.id ?? null)) {
                    setRoleValue(possibleDefaultValue);
                    return possibleDefaultValue;
                }
            }

            return null;
        },
        [projectUser.role?.id, projectRoleValues]
    );

    const [assignmentLoading, setAssignmentLoading] = useState(false);

    const changeAssignment = async (option) => {
        const previousIsOwner = isOwner;
        const previousRoleValue = roleValue;
        const shouldBeOwner = option.value === OWNER_ROLE_VALUE;
        const api = new ProjectApi(getAuthorityConfiguration());
        let ownershipChanged = false;

        setAssignmentLoading(true);
        try {
            if (shouldBeOwner !== isOwner) {
                await api.updateProjectOwner({
                    projectId: project.id,
                    userId: projectUser.user.id,
                    setOwnerInput: {isOwner: shouldBeOwner},
                });
                ownershipChanged = true;
            }

            if (!shouldBeOwner) {
                await api.updateProjectRole({
                    projectId: project.id,
                    userId: projectUser.user.id,
                    setRoleInput: {roleId: option.value},
                });
            }

            setIsOwner(shouldBeOwner);
            if (!shouldBeOwner) setRoleValue(option);
        } catch (e) {
            if (ownershipChanged) {
                try {
                    await api.updateProjectOwner({
                        projectId: project.id,
                        userId: projectUser.user.id,
                        setOwnerInput: {isOwner: previousIsOwner},
                    });
                } catch {
                    // The next server refresh remains authoritative if compensating the partial update fails.
                }
            }
            setIsOwner(previousIsOwner);
            setRoleValue(previousRoleValue);
            const message = await et(e);
            if (message) toast.error(message);
        } finally {
            setAssignmentLoading(false);
        }
    };

    const ownerRoleValue = projectRoleValues.find(({value}) => value === OWNER_ROLE_VALUE) ?? null;
    const selectedAssignment = isOwner ? ownerRoleValue : roleValue;
    const canEditAssignment = canChangeRole && (!isOwner || canToggleOwner);
    const loading = deleteState.downloading || assignmentLoading;

    return (
        <tr className={`${deleted ? 'hidden' : ''} ${loading ? 'pointer-events-none opacity-50' : ''}`}>
                    <td className="min-w-56">
                        <div className="flex items-center text-sm">
                            <IdentityImage
                                url={projectUser.user.avatarUrl}
                                user={projectUser.user}
                                className="mr-3 ring-2 ring-white dark:ring-slate-800"
                            />
                            <div className="col-name min-w-0">
                                <div className="truncate font-semibold text-slate-900 dark:text-white">
                                    {projectUser.user.firstName} {projectUser.user.lastName}
                                </div>
                                <div className="truncate text-xs text-slate-500 dark:text-slate-400" title={projectUser.user.email}>
                                    {projectUser.user.email}
                                </div>
                            </div>
                        </div>
                    </td>

                    <td className="min-w-64 text-right">
                        {canEditAssignment && <ProjectRoleSelect
                            options={projectRoleValues}
                            value={selectedAssignment}
                            onChange={changeAssignment}
                        />}
                        {!canEditAssignment && (isOwner
                            ? <Badge color="slate">
                                <FontAwesomeIcon icon={faCrown} className="mr-2 text-violet-600"/>
                                {selectedAssignment?.label}
                            </Badge>
                            : <Badge color="slate">{selectedAssignment?.label}</Badge>)}
                    </td>

                    <td className="whitespace-nowrap text-right text-sm text-slate-600 dark:text-slate-300">
                        {formatDateTimeLabel(projectUser.lastInteraction, false)}
                    </td>

                    <td className="w-12">
                        <div className="flex justify-end">
                            {canDelete &&
                                <ActionButton
                                    icon={'trash'}
                                    variant={'primary-red'}
                                    title={t('project.remove-user')}
                                    onClick={() => setDeleteQuestionVisible(true)}
                                />
                            }
                        </div>
                        {canDelete &&
                            <YesNo
                                title={t('confirmation')}
                                question={t('project.really-remove-user-question', {userName: projectUser.user.firstName + " " + projectUser.user.lastName})}
                                visible={deleteQuestionVisible}
                                setVisible={setDeleteQuestionVisible}
                                onYes={removeUser}
                            />
                        }
                    </td>
        </tr>
    );
}
